import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, ShieldCheck, KeyRound, Loader2, Copy, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";

/**
 * Re-usable 2FA enrolment / management section.
 * Used by both Admin Security page AND the role-agnostic Settings page so any
 * user (client, publisher, affiliate, admin) can self-enable TOTP.
 *
 * Props:
 *   - title:     Optional override of the section heading (default sane).
 *   - copyHint:  Optional override for the policy paragraph.
 */
export default function TwoFactorSection({ title = "Two-factor authentication (TOTP)", copyHint }) {
  const [status, setStatus] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);

  const load = () => api.get("/auth/2fa/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const beginEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable");
      setEnrollment(data);
      setBackupCodes(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start 2FA enrolment.");
    } finally { setBusy(false); }
  };

  const verifyEnroll = async () => {
    if (!code || code.length !== 6) {
      toast.error("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { code });
      setBackupCodes(data.codes);
      setEnrollment(null);
      setCode("");
      await load();
      toast.success("2FA enabled. Save your backup codes somewhere safe.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code. Try the most recent one from your app.");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    const c = window.prompt("Enter your current 2FA code (or a backup code):");
    if (!c) return;
    const pw = window.prompt("Confirm your password to disable 2FA:");
    if (!pw) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: c, password: pw });
      await load();
      toast.success("2FA disabled on this account.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disable 2FA.");
    } finally { setBusy(false); }
  };

  const copyCodes = async () => {
    if (!backupCodes) return;
    const ok = await copyToClipboard(backupCodes.join("\n"));
    if (ok) toast.success("Backup codes copied to clipboard.");
  };

  const hint = copyHint || "Add a second factor (Google Authenticator, 1Password, Authy) to your sign-in. Recommended for everyone, required for admins.";

  return (
    <>
      <Card className="rounded-xl border-slate-200 shadow-soft bg-white" data-testid="security-2fa-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0F172A] text-white shrink-0">
              <Lock className="size-5" strokeWidth={1.6} />
            </span>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-medium text-slate-900 text-[15px]">{title}</h3>
                {status?.enabled ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5">
                    <ShieldCheck className="size-3" /> Enabled
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">
                    <AlertTriangle className="size-3" /> Not enabled
                  </span>
                )}
              </div>
              <p className="text-[13px] text-slate-600 mt-2 max-w-xl leading-relaxed">{hint}</p>
              {status?.enabled && (
                <div className="mt-4 text-[12.5px] text-slate-600">
                  Enabled {status.enabled_at ? new Date(status.enabled_at).toLocaleDateString() : "—"} ·
                  <span className="text-slate-700 font-mono"> {status.backup_codes_remaining} backup code{status.backup_codes_remaining === 1 ? "" : "s"} remaining</span>
                </div>
              )}
              <div className="mt-5 flex gap-2 flex-wrap">
                {!status?.enabled && !enrollment && (
                  <Button onClick={beginEnroll} disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="2fa-begin-btn">
                    {busy ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />} Set up 2FA
                  </Button>
                )}
                {status?.enabled && (
                  <Button onClick={disable} disabled={busy} variant="outline" className="rounded-full text-rose-700 hover:bg-rose-50 hover:text-rose-700 border-rose-200" data-testid="2fa-disable-btn">
                    Disable 2FA
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {enrollment && (
        <Card className="rounded-xl border-slate-200 shadow-soft bg-white" data-testid="2fa-enroll-card">
          <CardContent className="p-6">
            <h4 className="font-medium text-slate-900 text-[15px]">Scan this QR code in your authenticator app</h4>
            <p className="text-[13px] text-slate-600 mt-1">Google Authenticator, 1Password, Authy or any TOTP app.</p>
            <div className="mt-5 grid sm:grid-cols-[180px_1fr] gap-6 items-start">
              <img src={enrollment.qr_png_b64} alt="2FA QR code" className="size-44 rounded-lg border border-slate-200 bg-white p-2" data-testid="2fa-qr" />
              <div className="space-y-4">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Or enter this secret manually</div>
                  <div className="mt-1 font-mono text-[12.5px] text-slate-900 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 break-all" data-testid="2fa-secret">
                    {enrollment.secret}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Enter the 6-digit code to confirm</div>
                  <div className="flex gap-2">
                    <Input
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="123 456"
                      className="font-mono text-lg tracking-[0.2em] max-w-[180px]"
                      data-testid="2fa-verify-input"
                    />
                    <Button onClick={verifyEnroll} disabled={busy || code.length !== 6} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="2fa-verify-btn">
                      {busy ? <Loader2 className="size-4 animate-spin" /> : "Verify"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {backupCodes && (
        <Card className="rounded-xl border-slate-200 shadow-soft bg-white" data-testid="2fa-backup-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0F172A] text-white shrink-0">
                <KeyRound className="size-5" strokeWidth={1.6} />
              </span>
              <div className="flex-1">
                <h4 className="font-medium text-slate-900 text-[15px]">Save these backup codes</h4>
                <p className="text-[13px] text-slate-600 mt-1">
                  Each code can be used once if you lose access to your authenticator app. Store them in a password manager — they will NOT be shown again.
                </p>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-2xl" data-testid="2fa-backup-codes">
                  {backupCodes.map((c) => (
                    <code key={c} className="font-mono text-[12.5px] text-slate-900 bg-slate-50 border border-slate-200 rounded px-3 py-2 text-center tracking-wider">
                      {c}
                    </code>
                  ))}
                </div>
                <div className="mt-4 flex gap-2">
                  <Button onClick={copyCodes} variant="outline" className="rounded-full" data-testid="2fa-backup-copy">
                    <Copy className="size-3.5" /> Copy all
                  </Button>
                  <Button onClick={() => setBackupCodes(null)} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="2fa-backup-done">
                    <Check className="size-3.5" /> I've saved them
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
